import { forwardRef, useEffect, useRef } from 'react';
import type { MouseEvent, PropsWithoutRef } from 'react';

import { Popover, Menu, Text, useI18n } from '@pega/cosmos-react-core';

import {
  StyledEmailMoreInfoPopover,
  StyledContextMenuFieldSelector,
  StyledContextMenu
} from './Email.styles';
import type { ContextMenuPopoverProps } from './Email.types';

const ContextMenuPopover = forwardRef(function ContextMenuPopover(
  { cursorPosition, contextMenu, targetNode, show }: PropsWithoutRef<ContextMenuPopoverProps>,
  ref: ContextMenuPopoverProps['ref']
) {
  const t = useI18n();
  const selectedText = useRef<string>();

  const { onItemClick, items } = contextMenu;
  const onItemSelect = (contextMenuItemId: string, e: MouseEvent) => {
    e.stopPropagation();
    const selectedcontextMenuItem = items.find(({ id }) => id === contextMenuItemId);

    if (selectedcontextMenuItem && selectedText.current) {
      onItemClick({
        fieldName: selectedcontextMenuItem.primary,
        fieldValue: selectedText.current
      });
    }
  };

  useEffect(() => {
    const selection = document.getSelection();

    if (!selection || !targetNode) return;

    if (targetNode.tagName.toLowerCase() === 'mark') {
      selection.removeAllRanges();
    }

    if (!selection.toString()) {
      const range = document.createRange();
      range.selectNodeContents(targetNode);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    if (selection.toString()) {
      selectedText.current = selection.toString();
    }
  }, [show, targetNode]);

  const itemsToRender = items.map(item => {
    return {
      ...item,
      visual: item.selected ? (
        <StyledContextMenuFieldSelector selected />
      ) : (
        <StyledContextMenuFieldSelector />
      )
    };
  });

  return (
    <Popover
      as={StyledEmailMoreInfoPopover}
      target={{
        getBoundingClientRect: () => {
          const { x, y } = cursorPosition;
          return {
            width: 0,
            height: 0,
            top: y,
            right: x,
            bottom: y,
            left: x,
            x,
            y,
            toJSON: () => {}
          };
        },
        contextElement: targetNode ?? undefined
      }}
      show={show}
      placement='bottom'
      ref={ref}
    >
      <StyledContextMenu>
        <Menu
          {...contextMenu}
          header={<Text variant='h5'>{t('copy_to')}</Text>}
          items={itemsToRender}
          onItemClick={onItemSelect}
        />
      </StyledContextMenu>
    </Popover>
  );
});

export default ContextMenuPopover;
